/**
 * Jobs Store
 *
 * Valtio store for job monitoring state and admin actions
 */
import { proxy } from "valtio";
import { db, tx, type JobStatus } from "@/lib/instantdb";

// Job consumer URL for triggering job processing
const JOB_CONSUMER_URL =
  import.meta.env.VITE_JOB_CONSUMER_URL ||
  "https://vizi-job-consumer.bpillow.workers.dev";

export type JobFilter = "all" | "queued" | "active" | "completed" | "failed";

interface JobsStore {
  // Filter state
  filter: JobFilter;
  selectedJobId: string | null;

  // Admin action state
  actionLoading: boolean;
  actionError: string | null;
}

export const jobsStore = proxy<JobsStore>({
  filter: "all",
  selectedJobId: null,
  actionLoading: false,
  actionError: null,
});

// ========== Filter Actions ==========

export function setFilter(filter: JobFilter) {
  jobsStore.filter = filter;
}

export function setSelectedJob(jobId: string | null) {
  jobsStore.selectedJobId = jobId;
}

// ========== Admin Actions ==========

/**
 * Retry a failed job - resets status and triggers job consumer
 */
export async function retryJob(jobId: string) {
  jobsStore.actionLoading = true;
  jobsStore.actionError = null;

  try {
    // 1. Update InstantDB status to queued
    await db.transact(
      tx.processing_jobs[jobId].update({
        status: "queued" as JobStatus,
        progress: 0,
        message: "Retrying...",
        error: null,
        completedAt: null,
        startedAt: Date.now(),
      }),
    );

    // 2. Trigger job consumer to process the job
    const response = await fetch(`${JOB_CONSUMER_URL}/jobs/${jobId}/process`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        (errorData as { error?: string }).error ||
          `Failed to trigger job processing: ${response.status}`
      );
    }
  } catch (e) {
    jobsStore.actionError =
      e instanceof Error ? e.message : "Failed to retry job";
    throw e;
  } finally {
    jobsStore.actionLoading = false;
  }
}

/**
 * Cancel a queued job - sets status to "failed" with cancellation message
 */
export async function cancelJob(jobId: string) {
  jobsStore.actionLoading = true;
  jobsStore.actionError = null;

  try {
    await db.transact(
      tx.processing_jobs[jobId].update({
        status: "failed" as JobStatus,
        error: "Cancelled by admin",
        completedAt: Date.now(),
      }),
    );
  } catch (e) {
    jobsStore.actionError =
      e instanceof Error ? e.message : "Failed to cancel job";
    throw e;
  } finally {
    jobsStore.actionLoading = false;
  }
}

/**
 * Delete a single job
 */
export async function deleteJob(jobId: string) {
  jobsStore.actionLoading = true;
  jobsStore.actionError = null;

  try {
    await db.transact(tx.processing_jobs[jobId].delete());
  } catch (e) {
    jobsStore.actionError =
      e instanceof Error ? e.message : "Failed to delete job";
    throw e;
  } finally {
    jobsStore.actionLoading = false;
  }
}

/**
 * Delete multiple jobs (bulk delete)
 */
export async function deleteJobs(jobIds: string[]) {
  jobsStore.actionLoading = true;
  jobsStore.actionError = null;

  try {
    const deletions = jobIds.map((id) => tx.processing_jobs[id].delete());
    await db.transact(deletions);
  } catch (e) {
    jobsStore.actionError =
      e instanceof Error ? e.message : "Failed to delete jobs";
    throw e;
  } finally {
    jobsStore.actionLoading = false;
  }
}

export function clearActionError() {
  jobsStore.actionError = null;
}

// ========== Filter Helpers ==========

/**
 * Check if a job status matches the current filter
 */
export function matchesFilter(status: JobStatus, filter: JobFilter): boolean {
  switch (filter) {
    case "all":
      return true;
    case "queued":
      return status === "queued";
    case "active":
      return (
        status === "uploading" ||
        status === "analyzing" ||
        status === "rendering"
      );
    case "completed":
      return status === "completed";
    case "failed":
      return status === "failed";
    default:
      return true;
  }
}
